import React, { useEffect, useState, useRef } from 'react'

import ActionToggle from 'live/components/ui/action-toggle'
import i18n from 'live/services/i18n'
import styled, { css } from 'styled-components'
import usePerspective from '../hooks/usePerspective'
import { PackageFeatureResource } from '../http/api'
import FeatureLabel from './FeatureLabel'
import Markdown from './Markdown'
import ModalActivationFeature from './ModalActivationFeature'
import ModalFeatureConfig from './ModalFeatureConfig'
import { Readme, Row } from './styles'
import { FeatureStatusTag } from './Tag'
import ToggleChevronButton from './ToggleChevronButton'
import ModalUnpackageDashboard from './ModalUnpackageDashboard'

type PropsFeature = {
    form
    pkgOriginallyEnabled
    setFeatureEnabled
    setFeatureSettings
    feature: PackageFeatureResource
}

const Feature = ({ feature, form, pkgOriginallyEnabled, setFeatureEnabled, setFeatureSettings }: PropsFeature) => {

    const [isUnpacking, setIsUnpaking] = useState<boolean>()

    // TODO: In feature.settings, solve the problem when features don't need settings
    const firstConfig = feature.settings === undefined

    const enabled = form.featuresEnabled?.[feature.uuid] || false

    const { perspectives, findPerspective } = usePerspective()

    const [perspective, setPerspective] = useState<string>()

    const [showDetails, setShowDetails] = useState(false)

    const [isOpenModalActivation, setIsOpenModalActivation] = useState(false)
    const [isOpenModalFeatureConfig, setIsOpenModalFeatureConfig] = useState(false)
    const [isOpenModalUnpackage, setIsOpenModalUnpackage] = useState(false)
    const perspectiveId = form.featuresSettings?.[feature.uuid]?.perspectiveId ?? feature.settings?.perspectiveId
    const isFeatureDisabled = feature.installed === undefined && form?.featuresSettings?.[feature?.uuid] === undefined

    const overwriteEntityId =
        form.featuresSettings?.[feature.uuid]?.overwriteEntityId ?? feature.settings?.overwriteEntityId

    useEffect(() => {
        if (perspectives !== undefined && perspectiveId) {
            const _perspective = findPerspective(perspectiveId)?.name

            setPerspective(_perspective || '-')
        } else {
            setPerspective('-')
        }
    }, [perspectiveId, perspectives])

    // useEffect(() => {
    //     setIsUnpaking(form.featuresSettings?.[feature.uuid]?.isUnpacking)
    // }, [form])

    const handleShowDetails = () => {
        setShowDetails(showDetails => !showDetails)
    }

    const handlePerspectiveChange = selectedId => {
        setFeatureSettings(feature.uuid, selectedId == null ? null : { perspectiveId: Number.parseInt(selectedId, 10) })
    }

    const handleToggle = () => {
        setIsOpenModalActivation(curr => !curr)
    }

    const callbackModalActivationFeature = (confirmed: boolean): void => {
        setIsOpenModalActivation(false)

        if (confirmed) setFeatureEnabled(feature.uuid, !enabled)
    }

    const callbackModalFeatureConfig = (resolve): Promise<void> => {
        return resolve(value => {
            const { confirmed, perspectiveId, overwriteEntityId } = value

            setIsOpenModalFeatureConfig(false)

            if (confirmed) {
                setFeatureEnabled(feature.uuid, true)
                handlePerspectiveChange(perspectiveId)
                setFeatureSettings(feature.uuid, { perspectiveId, overwriteEntityId })
            }
        })
    }

    const openModalUnpackageDashboardAndCloseModalFeatureConfig = (event: React.MouseEvent<HTMLAnchorElement>) => {
        setIsOpenModalUnpackage(true)
        setIsOpenModalFeatureConfig(false)
    }

    const unpackageDashboard = async (isUnpacking: boolean) => {
        setIsOpenModalUnpackage(false)

        setIsUnpaking(isUnpacking)

        if (isUnpacking === false) {
            setIsOpenModalFeatureConfig(true)
        } else {
            setFeatureEnabled(feature.uuid, false)
            setFeatureSettings(feature.uuid, { perspectiveId, isUnpacking })
        }
    }

    const cancelUnpacking = (event: React.MouseEvent<HTMLAnchorElement>) => {
        setIsUnpaking(false)
        setFeatureSettings(feature.uuid, { perspectiveId, overwriteEntityId })
    }

    return (
        <>
            <ModalActivationFeature
                feature={feature}
                isEnabled={!enabled && isUnpacking !== true}
                isOpen={isOpenModalActivation}
                callbackConfirm={callbackModalActivationFeature}
            />

            <ModalFeatureConfig
                feature={feature}
                isUnpacking={isUnpacking}
                perspectiveId={perspectiveId}
                cancelUnpacking={cancelUnpacking}
                isOpen={isOpenModalFeatureConfig}
                overwriteEntityId={overwriteEntityId}
                callbackConfirm={callbackModalFeatureConfig}
                unpackage={openModalUnpackageDashboardAndCloseModalFeatureConfig}
            />

            {/* <ModalUnpackageDashboard
                isOpen={isOpenModalUnpackage}
                callback={unpackageDashboard}
                dashboardName={`${feature?.name} [${feature?.version}]`}
            /> */}

            <Row className="feature-table-row" key={feature.uuid} showDetails={showDetails}>
                <div id={`feature-uuid-${feature?.uuid}-title`} className="col-md-4 feature-title">
                    <ActionToggle active={enabled} onChange={handleToggle} disabled={isFeatureDisabled} />

                    <FeatureLabel feature={feature} isUnpacked={isUnpacking} />
                </div>

                <div className="col-md-3">{isUnpacking ? '-' : perspective}</div>

                <div className="col-md-1">
                    <FeatureStatusTag
                        pkgOriginallyEnabled={pkgOriginallyEnabled}
                        feature={feature}
                        isFirstConfig={firstConfig}
                    />
                </div>

                <div className="col-md-1"></div>

                <div className="col-md-2">
                    <EditLink className="SubmitOrCancel__Cancel">
                        <a onClick={() => setIsOpenModalFeatureConfig(true)}>{i18n('Configure')}</a>
                    </EditLink>
                </div>

                <div className="col-md-1">
                    {feature.readme && <ToggleChevronButton toggle={showDetails} onClick={handleShowDetails} />}
                </div>
            </Row>

            {showDetails && feature.readme && (
                <Readme className="clearfix">
                    <Markdown headingStartLevel={4} text={feature.readme} />
                </Readme>
            )}
        </>
    )
}

type PropsFeatureDashboard = {
    form
    pkgOriginallyEnabled
    setFeatureEnabled
    setFeatureSettings
    features?: PackageFeatureResource[]
}

const FeatureDashboard = (props: PropsFeatureDashboard): JSX.Element => {
    return (
        <>
            <div className="feature-table-head">
                <div className="col-md-4"> {i18n('Dashboard Name')}</div>
                <div className="col-md-3"> {i18n('Perspective')}</div>
                <div className="col-md-1"> {i18n('Status')}</div>
                <div className="col-md-1" />
                <div className="col-md-2" />
                <div className="col-md-1" />
            </div>

            <div className="feature-table-body">
                {props?.features?.map((feature, index) => {
                    const propsFeature: PropsFeature = { ...props, feature }

                    return <Feature key={index} {...propsFeature} />
                })}
            </div>
        </>
    )
}

export default FeatureDashboard

const EditLink = styled.div`
    font-size: 14px;
    color: ${({ theme }) => theme['blue-500']};

    ${props =>
        props.disabled &&
        css`
            opacity: 0.65;
            pointer-events: none;
            cursor: default;
        `}
`
